/**
 * Bulk Buy Component JavaScript
 * Handles adding bulk quantities to cart via Shopify AJAX Cart API
 * 
 * Works with Shopify Basic plan - no apps required
 * 
 * Setup for discounts:
 * 1. Go to Shopify Admin > Discounts
 * 2. Create "Automatic discount"
 * 3. Set "Minimum purchase requirements" to quantity (e.g., 32 items)
 * 4. Set discount percentage (e.g., 5% off)
 * 5. The discount will apply automatically at checkout
 */

class BulkBuyComponent {
  constructor() {
    this.selectors = {
      component: '[data-bulk-buy-component]',
      addToCartBtn: '.bulk-buy__add-to-cart',
      savingsInfo: '.bulk-buy__savings-info'
    };
    
    this.init();
  }
  
  init() {
    // Bind click events to all bulk buy add to cart buttons
    document.querySelectorAll(this.selectors.addToCartBtn).forEach(button => {
      if (button.dataset.bulkBuyBound === 'true') return;
      button.dataset.bulkBuyBound = 'true';
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });
  }

  /**
   * Handle add to cart button click
   * @param {Event} event - Click event
   */
  async handleAddToCart(event) {
    event.preventDefault();
    const button = event.currentTarget;
    
    // Get data attributes
    const variantId = button.dataset.variantId;
    const quantity = parseInt(button.dataset.quantity, 10);
    const discount = button.dataset.discount;
    const productTitle = button.dataset.productTitle;
    
    if (!variantId || !quantity) {
      console.error('Bulk Buy: Missing variant ID or quantity');
      return;
    }
    
    // Disable button and show loading state
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';
    button.classList.add('bulk-buy__offer-cta--loading');
    
    try {
      // Add to cart using Shopify AJAX API
      const response = await this.addToCart(variantId, quantity, {
        _bulk_buy: 'true',
        _bulk_quantity: quantity.toString(),
        _bulk_discount: discount + '%'
      });
      
      if (response.ok) {
        const item = await response.json();
        
        // Fetch updated cart
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        // Success - update button text temporarily
        button.textContent = 'Added!';
        button.classList.add('bulk-buy__offer-cta--success');

        // Update cart count in header (if cart count element exists)
        this.updateCartCount();

        // Dispatch event for our custom cart notification popup
        // Pass the item from response, cart data, and the quantity we just added
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { 
            item: {
              ...item.items ? item.items[0] : item,
              product_title: productTitle,
              title: productTitle
            }, 
            cart: cart,
            quantity: quantity // Pass the bulk quantity we added
          }
        }));
        
        // Reset button after delay
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
          button.classList.remove('bulk-buy__offer-cta--loading', 'bulk-buy__offer-cta--success');
        }, 2000);
        
      } else {
        throw new Error('Failed to add to cart');
      }
      
    } catch (error) {
      console.error('Bulk Buy: Add to cart failed', error);
      button.textContent = 'This Items Inventory Is Less';
      button.classList.add('bulk-buy__offer-cta--error');
      
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('bulk-buy__offer-cta--loading', 'bulk-buy__offer-cta--error');
      }, 2000);
    }
  }
  
  /**
   * Add item to cart using Shopify AJAX Cart API
   * @param {string} variantId - Product variant ID
   * @param {number} quantity - Quantity to add
   * @param {object} properties - Line item properties (for tracking bulk purchases)
   * @returns {Promise<Response>}
   */
  async addToCart(variantId, quantity, properties = {}) {
    const formData = {
      items: [{
        id: parseInt(variantId, 10),
        quantity: quantity,
        properties: properties
      }]
    };
    
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    });
    
    return response;
  }
  
  /**
   * Update cart count in header and refresh cart UI sections
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update various cart count elements (adjust selectors as needed for your theme)
      const cartCountSelectors = [
        '.cart-count-bubble',
        '.cart-count',
        '[data-cart-count]',
        '.header__cart-count',
        '#cart-icon-bubble span',
        '.header-cart-badge'
      ];
      
      cartCountSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          // Don't update if element is an SVG wrapper
          if (!el.classList.contains('svg-wrapper')) {
            el.textContent = cart.item_count;
            el.style.display = cart.item_count > 0 ? '' : 'none';
          }
        });
      });
      
      // Use Shopify section rendering API to refresh cart sections
      await this.refreshCartSections();
      
      // Dispatch custom event for other scripts to listen to
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart: cart } 
      }));
      
    } catch (error) {
      console.error('Failed to update cart count', error);
    }
  }

  /**
   * Refresh cart sections using Shopify's section rendering API
   * This ensures the cart drawer and icon bubble reflect the current cart state
   */
  async refreshCartSections() {
    try {
      const sections = 'cart-drawer,cart-icon-bubble';
      const response = await fetch(`${window.location.pathname}?sections=${sections}`);
      
      if (!response.ok) throw new Error('Failed to fetch cart sections');
      
      const sectionsData = await response.json();
      
      // Update cart drawer content
      if (sectionsData['cart-drawer']) {
        const cartDrawerElement = document.getElementById('CartDrawer');
        if (cartDrawerElement) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionsData['cart-drawer'], 'text/html');
          const newContent = doc.querySelector('#CartDrawer');
          if (newContent) {
            cartDrawerElement.innerHTML = newContent.innerHTML;
          }
        }
      }

      // Sync empty state class on the <cart-drawer> wrapper
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const isEmpty = cart.item_count === 0;
          cartDrawer.classList.toggle('is-empty', isEmpty);
          cartDrawer.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);
        }
      } catch (e) {
        console.warn('Failed to sync cart drawer empty state:', e);
      }
      
      // Update cart icon bubble
      if (sectionsData['cart-icon-bubble']) {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionsData['cart-icon-bubble'], 'text/html');
          const newIconContent = doc.querySelector('.shopify-section');
          if (newIconContent) {
            // Update just the badge, not the entire icon
            const newBadge = newIconContent.querySelector('.header-cart-badge, .cart-count-bubble');
            const existingBadge = cartIconBubble.querySelector('.header-cart-badge, .cart-count-bubble');
            if (newBadge && existingBadge) {
              existingBadge.textContent = newBadge.textContent;
              existingBadge.style.display = '';
            } else if (newBadge) {
              // Badge didn't exist before, need to add it
              cartIconBubble.appendChild(newBadge.cloneNode(true));
            } else if (!newBadge && existingBadge) {
              existingBadge.remove();
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh cart sections:', error);
    }
  }
  
  /**
   * Show cart notification/drawer (fallback if custom popup not available)
   * Note: Our custom cart-notification-popup.js listens for 'cart:item-added' event
   * @param {string} productTitle - Product title
   * @param {number} quantity - Quantity added
   */
  showCartNotification(productTitle, quantity) {
    // The custom popup handles this via the 'cart:item-added' event
    // This is now just a fallback toast notification
    const message = `Added ${quantity}x ${productTitle} to cart!\n\nBulk discount will be applied at checkout.`;
    this.showToast(message);
  }
  
  /**
   * Show toast notification
   * @param {string} message - Message to display
   */
  showToast(message) {
    // Check if toast container exists, create if not
    let toastContainer = document.querySelector('.bulk-buy-toast-container');
    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.className = 'bulk-buy-toast-container';
      document.body.appendChild(toastContainer);
    }
    
    // Create toast element
    const toast = document.createElement('div');
    toast.className = 'bulk-buy-toast';
    toast.innerHTML = `
      <div class="bulk-buy-toast__content">
        <svg class="bulk-buy-toast__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <span>${message.replace(/\n/g, '<br>')}</span>
      </div>
      <button class="bulk-buy-toast__close" aria-label="Close">&times;</button>
    `;
    
    // Add close functionality
    toast.querySelector('.bulk-buy-toast__close').addEventListener('click', () => {
      toast.remove();
    });
    
    // Add to container
    toastContainer.appendChild(toast);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      toast.classList.add('bulk-buy-toast--hiding');
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new BulkBuyComponent());
} else {
  new BulkBuyComponent();
}

// Also reinitialize when Shopify section is re-rendered (for theme editor)
document.addEventListener('shopify:section:load', () => new BulkBuyComponent());
